"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { animate, motion, useMotionValue } from "framer-motion";
import { useTranslations } from "next-intl";

import { ShinyButton } from "@/components/ui/shiny-button";
import NewsCard from "./NewsCard";
import ResponsiveCopy from "../../ui/ResponsiveCopy";

// Swipe or select a compact page dot to navigate. Dots count viewport-wide
// pages, with the last target clamped to the end of the track.

const SPRING = { type: "spring", stiffness: 260, damping: 36 };

// px/s past which a release counts as a flick: a quick swipe should turn the page
// even if the finger travelled less than half a card. Below it, the deck goes
// back to whichever card it is closest to.
const FLICK_VELOCITY = 400;

// Which dot the track is closest to. Nearest-target rather than dividing by the
// viewport width, because the last page's target is clamped to where the track
// can actually stop — it is no longer a whole multiple, and dividing would leave
// the final dot unlit at the end of the deck.
function nearestPage(pages, v) {
  let best = 0;
  for (let i = 1; i < pages.length; i += 1) {
    if (Math.abs(pages[i] - v) < Math.abs(pages[best] - v)) best = i;
  }
  return best;
}

export default function NewsCarousel({ blogs }) {
  const t = useTranslations("home.news");
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const x = useMotionValue(0);

  // A mouse drag ends with a click on whatever is under the pointer, and every
  // card is a link — so a drag used to open a post. Touch needs none of this:
  // the browser already withholds the click once the finger has travelled.
  const draggedRef = useRef(false);

  const [maxScroll, setMaxScroll] = useState(0); // positive px the track can move left
  const [viewport, setViewport] = useState(0); // px of track visible at once
  const [page, setPage] = useState(0);

  // Re-measure how far the track overflows its viewport on mount + resize.
  useEffect(() => {
    const measure = () => {
      const vp = viewportRef.current;
      const track = trackRef.current;
      if (!vp || !track) return;
      const overflow = Math.max(0, track.scrollWidth - vp.offsetWidth);
      setMaxScroll(overflow);
      setViewport(vp.offsetWidth);
      // Clamp current position if the viewport grew.
      if (x.get() < -overflow) animate(x, -overflow, { duration: 0.2 });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [x, blogs]);

  // Resting positions for the dots, one per screenful. Every target is clamped
  // to the track's own limit so the last dot points at somewhere the deck can
  // stop, rather than at a position it would spring straight back from. Empty
  // when nothing overflows — a deck that cannot move should not advertise dots.
  const pages = useMemo(() => {
    if (!viewport || maxScroll <= 0) return [];
    const count = Math.ceil((maxScroll + viewport) / viewport);
    return Array.from({ length: count }, (_, i) =>
      Math.max(-maxScroll, -i * viewport),
    );
  }, [maxScroll, viewport]);

  // Keep the active dot in sync with the live track position.
  useEffect(() => {
    const update = (v) => {
      setPage(nearestPage(pages, v));
    };
    update(x.get());
    const unsub = x.on("change", update);
    return unsub;
  }, [pages, x, maxScroll]);

  const step = useCallback(() => {
    const track = trackRef.current;
    const cardWidth = track?.firstElementChild?.offsetWidth ?? 360;
    // Read the gap rather than hardcoding it: it is narrower on a phone, where
    // the card is nearly the full viewport.
    const gap = track
      ? Number.parseFloat(window.getComputedStyle(track).columnGap) || 0
      : 0;
    return cardWidth + gap;
  }, []);

  // Where the track is resting, measured in cards. Fractional mid-drag, and
  // fractional at the very end too: the last card's grid slot usually sits past
  // the clamp, so the deck stops short of it.
  const position = useCallback(() => -x.get() / step(), [step, x]);

  const settle = useCallback(
    (index) => {
      const target = Math.min(0, Math.max(-maxScroll, -index * step()));
      animate(x, target, SPRING);
    },
    [maxScroll, step, x],
  );

  // Floor/ceil rather than rounding the flick: from a card the deck is already
  // resting on, `Math.ceil` would return that same card and a flick would do
  // nothing.
  const onDragEnd = useCallback(
    (_event, info) => {
      const at = position();
      const velocity = info.velocity.x;
      if (velocity < -FLICK_VELOCITY) settle(Math.floor(at) + 1);
      else if (velocity > FLICK_VELOCITY) settle(Math.ceil(at) - 1);
      else settle(Math.round(at));
    },
    [position, settle],
  );

  return (
    <div>
      {/* Stack the heading and blog link on narrow screens. */}
      <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-3xl font-bold text-white sm:text-5xl lg:text-6xl">
          <ResponsiveCopy desktop={t("title")} mobile={t("titleMobile")} />
        </h2>
        {/* `neutral`, because this band is bg-neutral-900 — see ShinyButton's
            TONE_STYLE for what the default fill does there. */}
        <ShinyButton href="/blog" tone="neutral" className="shrink-0">
          {t("viewAll")}
        </ShinyButton>
      </div>

      {/* Draggable track. Below sm it bleeds past the section's right padding to
          the screen edge, so a slice of the next card shows instead of a dead
          gutter; the track's own pr-6 gives the last card that margin back when
          the drag reaches the end. */}
      <div ref={viewportRef} className="-mr-6 mt-14 overflow-hidden sm:mr-0">
        <motion.div
          ref={trackRef}
          className="flex w-max cursor-grab gap-4 pr-6 active:cursor-grabbing sm:gap-11 sm:pr-0"
          style={{ x }}
          drag="x"
          dragConstraints={{ left: -maxScroll, right: 0 }}
          dragElastic={0.08}
          // Momentum off: the release is handled by onDragEnd, and framer's own
          // inertia would be animating x towards a different resting place.
          dragMomentum={false}
          // Reset per gesture, not per drag: on touch the browser swallows the
          // click a swipe ends with, so the flag would still be up when the next
          // tap arrives — and a tap never starts a drag to clear it.
          onPointerDownCapture={() => {
            draggedRef.current = false;
          }}
          // A few pixels of slop, so a click that trembles still counts as a
          // click.
          onDrag={(_event, info) => {
            if (Math.abs(info.offset.x) > 6) draggedRef.current = true;
          }}
          onDragEnd={onDragEnd}
          // Capture: the click has to die before it reaches the card's link.
          onClickCapture={(event) => {
            if (!draggedRef.current) return;
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {blogs.map((blog, i) => (
            <NewsCard key={blog?.slug ?? i} blog={blog} />
          ))}
        </motion.div>
      </div>

      {/* Compact, tappable page indicators; swiping remains available. */}
      <div className="mt-8 flex items-center justify-center">
        {pages.length > 1 && (
          <div className="flex items-center justify-center gap-0">
            {pages.map((target, i) => (
              <button
                key={i}
                type="button"
                onClick={() => animate(x, target, SPRING)}
                aria-label={t("goToPage", { page: i + 1 })}
                aria-current={page === i}
                className="flex h-11 w-6 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue"
              >
                <span aria-hidden className={`h-1.5 rounded-full transition-[width,background-color] duration-200 motion-reduce:transition-none ${page === i ? "w-5 bg-sky-blue" : "w-1.5 bg-white/40"}`} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
